import { useId } from "react";
import Svg, { Circle, Defs, LinearGradient, Path, Rect, Stop, Text as SvgText } from "react-native-svg";
import { Body3, Button, Card, Display4, Subtitle1, useTheme, XStack, YStack } from "@medbillai/ui";
import { useRouter } from "@medbillai/navigation/router";
import { billsUrl } from "@medbillai/utils/urls";
import { analyticsService } from "../../../lib/analytics";
import { markOpenedInApp } from "../../../lib/openedInApp";
import { SingularEvents, singularService } from "../../../lib/singular";
import { usePushGetHelp } from "../../../lib/usePushGetHelp";
import { useBillsClaimsRedesign } from "../../bill/BillsTab";
import { RecordRow } from "../../bill/records/RecordRow";
import { type RecordItem, type RecordKind, recordUrl } from "../../bill/records/records";
import { useRecords } from "../../bill/records/useRecords";
import { useInsuranceLinked } from "../useInsuranceLinked";
const SHOWN = 4;

// The legacy bill detail opens only claims and invoices.
const LEGACY_DETAIL_KINDS: ReadonlySet<RecordKind> = new Set(["claim", "invoice"]);
export function BillsClaimsSection({
  showsTotals,
  onLinkInsurance
}: {
  showsTotals: boolean;
  onLinkInsurance: () => void;
}) {
  const {
    records,
    loading,
    error
  } = useRecords();
  const redesign = useBillsClaimsRedesign();
  const shown = records.filter(record => redesign || LEGACY_DETAIL_KINDS.has(record.kind)).slice(0, SHOWN);
  return shown.length > 0 ? <RecentRecords records={shown} data-sentry-element="RecentRecords" data-sentry-component="BillsClaimsSection" data-sentry-source-file="BillsClaimsSection.tsx" />
  // The no-records card's copy speaks to the totals, so only beside them.
  : showsTotals && records.length === 0 && !loading && !error ? <NoRecordsYet onLinkInsurance={onLinkInsurance} /> : null;
}
function RecentRecords({
  records
}: {
  records: RecordItem[];
}) {
  const {
    push
  } = useRouter();
  return <YStack marginTop="$sp3" gap="$sp1" data-sentry-element="YStack" data-sentry-component="RecentRecords" data-sentry-source-file="BillsClaimsSection.tsx">
      <XStack alignItems="center" justifyContent="space-between" data-sentry-element="XStack" data-sentry-source-file="BillsClaimsSection.tsx">
        <Subtitle1 color="$textDefault" data-sentry-element="Subtitle1" data-sentry-source-file="BillsClaimsSection.tsx">Bills & claims</Subtitle1>
        <Button variant="tertiary" size="small" fillWidth={false} onPress={() => {
        analyticsService.trackEvent("CoverageBillsClaimsTapped", {
          target: "seeAll"
        });
        markOpenedInApp(billsUrl());
        push(billsUrl());
      }} data-sentry-element="Button" data-sentry-source-file="BillsClaimsSection.tsx">
          See all
        </Button>
      </XStack>
      {records.map(record => <RecordRow key={`${record.kind}:${record.key}`} record={record} onPress={() => {
      singularService.event(SingularEvents.openBill);
      analyticsService.trackEvent("CoverageBillsClaimsTapped", {
        target: "record",
        kind: record.kind
      });
      push(recordUrl(record));
    }} />)}
    </YStack>;
}
function NoRecordsYet({
  onLinkInsurance
}: {
  onLinkInsurance: () => void;
}) {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const askToLink = useInsuranceLinked() === false;
  return <YStack marginTop="$sp3" gap="$sp1" data-sentry-element="YStack" data-sentry-component="NoRecordsYet" data-sentry-source-file="BillsClaimsSection.tsx">
      <Subtitle1 color="$textDefault" data-sentry-element="Subtitle1" data-sentry-source-file="BillsClaimsSection.tsx">Bills & claims</Subtitle1>
      <Card variant="raised" size="large" padding="$sp2" testID="coverage.bills-claims-empty" data-sentry-element="Card" data-sentry-source-file="BillsClaimsSection.tsx">
        <StepIllustration data-sentry-element="StepIllustration" data-sentry-source-file="BillsClaimsSection.tsx" />
        <Display4 color="$textDefault" marginTop="$sp2" marginBottom="$sp1" data-sentry-element="Display4" data-sentry-source-file="BillsClaimsSection.tsx">
          Build the story behind these totals.
        </Display4>
        <Body3 color="$textSubtle" data-sentry-element="Body3" data-sentry-source-file="BillsClaimsSection.tsx">
          {askToLink ? "Link the insurer for claim history, or add bills already on hand." : "Add bills already on hand."}{" "}
          We’ll connect them to deductible changes as new checks arrive.
        </Body3>
        <XStack gap="$sp1" marginTop="$sp2" data-sentry-element="XStack" data-sentry-source-file="BillsClaimsSection.tsx">
          {askToLink ? <Button variant="brand" flex={1} onPress={onLinkInsurance}>
              Link insurance
            </Button> : null}
          <Button variant="secondary" flex={1} onPress={() => {
          analyticsService.trackEvent("CoverageBillsClaimsTapped", {
            target: "addBill"
          });
          pushGetHelp({
            caseType: "BILL_ISSUE_V2",
            title: "Add a bill",
            source: "coverage_bills_claims_add_bill"
          });
        }} data-sentry-element="Button" data-sentry-source-file="BillsClaimsSection.tsx">
            Add a bill
          </Button>
        </XStack>
      </Card>
    </YStack>;
}
const STEP_LINE = "M24 108 H74 V96 H124 V84 H168 V70 H218 V58 H258 V50 H296";

/** An illustration: its geometry is the prototype's own, outside the DS. */
function StepIllustration() {
  const theme = useTheme();
  const id = useId().replace(/[^\w-]/g, "");
  const dark = theme.surfaceInverse.val;
  const lime = theme.bgBrandIntense.val;
  const pale = theme.bgBrand.val;
  return <YStack height="$si16" borderRadius="$br2" overflow="hidden" backgroundColor="$bgBrand" aria-hidden data-sentry-element="YStack" data-sentry-component="StepIllustration" data-sentry-source-file="BillsClaimsSection.tsx">
      <Svg width="100%" height="100%" viewBox="0 0 320 126" data-sentry-element="Svg" data-sentry-source-file="BillsClaimsSection.tsx">
        <Defs data-sentry-element="Defs" data-sentry-source-file="BillsClaimsSection.tsx">
          <LinearGradient id={`${id}-fill`} x1="0" y1="0" x2="0" y2="1" data-sentry-element="LinearGradient" data-sentry-source-file="BillsClaimsSection.tsx">
            <Stop offset="0" stopColor={lime} stopOpacity={0.7} data-sentry-element="Stop" data-sentry-source-file="BillsClaimsSection.tsx" />
            <Stop offset="1" stopColor={lime} stopOpacity={0} data-sentry-element="Stop" data-sentry-source-file="BillsClaimsSection.tsx" />
          </LinearGradient>
        </Defs>
        <Path d="M24 34 H296" stroke={dark} strokeOpacity={0.3} strokeWidth={1.2} strokeDasharray="3 5" data-sentry-element="Path" data-sentry-source-file="BillsClaimsSection.tsx" />
        <Rect x={24} y={26} width={62} height={16} rx={8} fill={dark} data-sentry-element="Rect" data-sentry-source-file="BillsClaimsSection.tsx" />
        <SvgText x={55} y={37.5} textAnchor="middle" fontSize={8.5} fill={pale} data-sentry-element="SvgText" data-sentry-source-file="BillsClaimsSection.tsx">
          Deductible
        </SvgText>
        <Path d={`${STEP_LINE} V126 H24Z`} fill={`url(#${id}-fill)`} data-sentry-element="Path" data-sentry-source-file="BillsClaimsSection.tsx" />
        <Path d={STEP_LINE} fill="none" stroke={dark} strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" data-sentry-element="Path" data-sentry-source-file="BillsClaimsSection.tsx" />
        <Circle cx={296} cy={50} r={5} fill={lime} stroke={dark} strokeWidth={2.5} data-sentry-element="Circle" data-sentry-source-file="BillsClaimsSection.tsx" />
      </Svg>
    </YStack>;
}